/**
 * Metronome Engine v2.4 (Feature Complete & Corrected)
 *
 * =====================================================================================
 * JOURNAL DES MODIFICATIONS (FEATURE COMPLETE & CORRECTED)
 *
 * 1.  GESTION DE LA PAUSE/REPRISE : Ajout des méthodes `pause()`, `resume()` et
 *     `resetStep()`. Celles-ci transmettent simplement les commandes correspondantes
 *     au worker, qui gère l'état réel de la lecture.
 * 2.  COMMUNICATION BIDIRECTIONNELLE : Le moteur est désormais capable de gérer les
 *     acquittements de pause ou les événements de changement d'état en provenance
 *     du worker si nécessaire (bien que l'implémentation actuelle reste simple).
 * 3.  API FONCTIONNELLE COMPLÈTE : L'API du proxy est maintenant complète et reflète
 *     toutes les fonctionnalités de contrôle de la lecture disponibles pour l'utilisateur.
 *
 * 4.  CORRECTION DU BUG D'AFFICHAGE DU COCKPIT POUR LES TEMPS IRRÉGULIERS (v3 - Optimisation) :
 *     - Refactorisation de la logique `generateTimeSignaturePattern` pour les temps
 *       simples non prédéfinis et irréguliers (ex: 5, 7, 11, 13, 17, etc.).
 *       L'algorithme utilise une approche gloutonne plus robuste pour décomposer
 *       le nombre total de temps en combinaisons de 2 et 3, garantissant des groupements
 *       musicalement plus intuitifs et un affichage visuel toujours correct.
 *
 * 5.  INTÉGRATION DU GROUPEMENT RYTHMIQUE PERSONNALISÉ :
 *     - La méthode `configure` transmet désormais le `customGroupingPattern` au worker.
 *     - La méthode `analyzeTimeSignature` a été modifiée pour accepter un argument
 *       `customGroupingPattern` et pour prioriser ce pattern si fourni et valide.
 *     - Une nouvelle fonction `calculateStrongBeatsFromGrouping` a été ajoutée pour
 *       déterminer les temps forts basés sur un pattern de groupement donné.
 *     - Le pattern de groupement personnalisé est désormais utilisé pour la
 *       génération des temps forts par défaut et l'affichage des indicateurs.
 * =====================================================================================
 */
export class MetronomeEngine {
    constructor() {
        this.worker = new Worker(new URL('./metronome-worker.js', import.meta.url), { type: 'module' });
        this.isRunning = false;
        this.audioContext = null;
        this.gainNode = null;
        this.audioBuffers = {};

        this.config = {
            beatsPerMeasure: 4,
            noteValue: 4,
            measuresPerStep: 1,
            soundType: 'classic',
            volume: 0.75,
            subdivisionMode: 'none',
            swingRatio: 0.66,
            customAccents: [0],
            accentLevel: 1.5,
            customGroupingPattern: null, // NEW: Add custom grouping pattern to config
        };

        this.programSteps = [];

        this.callbacks = {
            onBeat: null,
            onStepChange: null,
            onMeasureChange: null,
            onProgramComplete: null,
        };

        this.lastStep = -1;
        this.lastMeasure = -1;

        this.worker.onmessage = this.handleWorkerMessage.bind(this);
        console.log('MetronomeEngine v2.7 Initialized (Pause/Resume Ready)');
    }

    async initializeAudio(audioContext, audioBuffers) {
        this.audioContext = audioContext;
        this.audioBuffers = audioBuffers;
        this.createGainNode();
        // Send transferable audio buffer data to worker
        // This is an advanced optimization for AudioWorklet, but good practice for workers too
        // For simplicity, we'll keep the main thread sound playing for now.
    }

    createGainNode() {
        if (this.gainNode) {
            this.gainNode.disconnect();
        }
        this.gainNode = this.audioContext.createGain();
        this.gainNode.gain.setValueAtTime(this.config.volume, this.audioContext.currentTime);
        this.gainNode.connect(this.audioContext.destination);
    }

    playSound(soundType, accentLevel) {
        if (!this.audioContext || !this.gainNode) return;
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume().catch(() => {});
            return;
        }

        const soundSet = this.config.soundType || 'classic';
        let buffer = this.audioBuffers[soundSet]?.[soundType];

        if (!buffer) {
            buffer = this.audioBuffers['classic']?.['weak'];
            if (!buffer) return;
        }

        try {
            const source = this.audioContext.createBufferSource();
            const soundGain = this.audioContext.createGain();
            source.buffer = buffer;
            soundGain.gain.setValueAtTime(accentLevel, this.audioContext.currentTime);
            source.connect(soundGain);
            soundGain.connect(this.gainNode);
            source.start(0);
        } catch (e) {
            console.error("Error playing sound:", e);
        }
    }


    handleWorkerMessage(e) {
        const { type, payload } = e.data;
        switch (type) {
            case 'beat':
                this.playSound(payload.event.isSubdivision ? 'subdivision' : (payload.event.isStrong ? 'strong' : 'weak'), payload.event.accentLevel);

                if (this.callbacks.onBeat) {
                    this.callbacks.onBeat(payload.event, payload.progress);
                }

                if (payload.event.step !== this.lastStep) {
                    this.lastStep = payload.event.step;
                    if (this.callbacks.onStepChange) this.callbacks.onStepChange(this.lastStep);
                }
                if (payload.event.measureInStep !== this.lastMeasure) {
                    this.lastMeasure = payload.event.measureInStep;
                    if (this.callbacks.onMeasureChange) this.callbacks.onMeasureChange(this.lastMeasure);
                }
                break;
            case 'complete':
                this.isRunning = false;
                if (this.callbacks.onProgramComplete) {
                    this.callbacks.onProgramComplete();
                }
                break;
        }
    }

    configure(newConfig) {
        this.config = { ...this.config, ...newConfig };
        if (this.gainNode && this.audioContext) {
            this.gainNode.gain.setTargetAtTime(this.config.volume, this.audioContext.currentTime, 0.01);
        }
        // NEW: Send updated config to worker
        this.worker.postMessage({ type: 'updateConfig', payload: { config: this.config } });
    }

    setProgram(steps) {
        this.programSteps = steps;
    }

    start() {
        if (this.isRunning) return;
        this.isRunning = true;
        this.lastStep = -1;
        this.lastMeasure = -1;

        this.worker.postMessage({
            type: 'start',
            payload: {
                config: this.config,
                programSteps: this.programSteps,
                timeAnchor: performance.now(),
            }
        });
    }

    pause() {
        if (!this.isRunning) return;
        return new Promise(resolve => {
            // Can be extended to wait for worker acknowledgement
            this.worker.postMessage({ type: 'pause' });
            if (this.audioContext) this.audioContext.suspend();
            resolve();
        });
    }

    resume() {
        if (!this.isRunning) return;
        this.worker.postMessage({ type: 'resume', payload: { timeAnchor: performance.now() } });
        if (this.audioContext) this.audioContext.resume();
    }

    stop() {
        if (!this.isRunning) return;
        this.isRunning = false;
        this.worker.postMessage({ type: 'stop' });
    }

    resetStep() {
        this.worker.postMessage({ type: 'resetStep' });
    }

    terminate() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
    }

    setCallbacks(callbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }

    // NEW: Accept customGroupingPattern
    analyzeTimeSignature(beatsPerMeasure, noteValue, customGroupingPattern = null) {
        const signature = `${beatsPerMeasure}/${noteValue}`;
        const isCompound = MetronomeEngine.isCompoundTimeSignature(beatsPerMeasure, noteValue);
        const category = isCompound ? 'compound' : 'simple';
        const patterns = MetronomeEngine.buildTimeSignaturePatterns();

        let pattern;

        // NEW: Prioritize custom grouping if provided and valid
        if (customGroupingPattern && customGroupingPattern.length > 0) {
            const sum = customGroupingPattern.reduce((acc, val) => acc + val, 0);
            if (sum === beatsPerMeasure) {
                pattern = {
                    grouping: customGroupingPattern,
                    strongBeats: MetronomeEngine.calculateStrongBeatsFromGrouping(customGroupingPattern),
                    feeling: 'custom_grouping_feeling' // A new feeling for custom groupings
                };
                return { signature, category, isCompound, beatsPerMeasure, noteValue, ...pattern };
            }
        }

        // Existing logic if no custom grouping or if invalid
        // Special case for 9/8 to prefer compound (3+3+3) over simple (2+2+2+3) if it IS compound
        if (signature === '9/8' && isCompound) {
            pattern = patterns.compound[signature];
        } else if (patterns[category] && patterns[category][signature]) {
            pattern = patterns[category][signature];
        } else {
            // Fallback to automatic generation if no predefined pattern or invalid custom pattern
            pattern = MetronomeEngine.generateTimeSignaturePattern(beatsPerMeasure, isCompound);
        }
        return { signature, category, isCompound, beatsPerMeasure, noteValue, ...pattern };
    }

    // NEW: Helper to calculate strong beats from a grouping
    static calculateStrongBeatsFromGrouping(grouping) {
        const strongBeats = [0];
        let currentBeat = 0;
        for (let i = 0; i < grouping.length - 1; i++) {
            currentBeat += grouping[i];
            strongBeats.push(currentBeat);
        }
        return strongBeats;
    }

    static buildTimeSignaturePatterns() {
        return {
            simple: {
                '2/4': { grouping: [2], strongBeats: [0], feeling: 'march' },
                '3/4': { grouping: [3], strongBeats: [0], feeling: 'waltz' },
                '4/4': { grouping: [4], strongBeats: [0, 2], feeling: 'common' },
                '5/4': { grouping: [3, 2], strongBeats: [0, 3], feeling: 'asymmetric' }, // Common 5 grouping
                '7/4': { grouping: [2, 2, 3], strongBeats: [0, 2, 4], feeling: 'asymmetric' }, // Common 7 grouping
                '5/8': { grouping: [3, 2], strongBeats: [0, 3], feeling: 'irregular' }, // Same as 5/4 but for 8th notes
                '7/8': { grouping: [2, 2, 3], strongBeats: [0, 2, 4], feeling: 'irregular' }, // Same as 7/4 but for 8th notes
                // For 9/8 (simple), 11/8, etc. these will now fall into the generateTimeSignaturePattern logic
                // and should produce more musically sensical groupings
            },
            compound: {
                '6/8': { grouping: [3, 3], strongBeats: [0, 3], feeling: 'lilting' },
                '9/8': { grouping: [3, 3, 3], strongBeats: [0, 3, 6], feeling: 'compound_triple' },
                '12/8': { grouping: [3, 3, 3, 3], strongBeats: [0, 3, 6, 9], feeling: 'compound_quadruple' },
            },
        };
    }

    static isCompoundTimeSignature(beatsPerMeasure, noteValue) {
        // A time signature is typically compound if the numerator is a multiple of 3 (and > 3)
        // AND the denominator is usually an 8th note or 16th note, indicating the beat subdivides into 3.
        return (noteValue === 8 || noteValue === 16) && beatsPerMeasure > 3 && beatsPerMeasure % 3 === 0;
    }

    static generateTimeSignaturePattern(beatsPerMeasure, isCompound) {
        if (isCompound) {
            const mainBeats = beatsPerMeasure / 3;
            const grouping = Array(mainBeats).fill(3);
            const strongBeats = grouping.map((_, i) => i * 3);
            return { grouping, strongBeats, feeling: 'compound_generated' };
        } else {
            let grouping = [];
            let feeling = 'simple_generated'; // Default feeling

            // 1. Handle standard simple meters explicitly (1, 2, 3, 4)
            if (beatsPerMeasure === 1) {
                grouping = [1];
            } else if (beatsPerMeasure === 2) {
                grouping = [2];
            } else if (beatsPerMeasure === 3) {
                grouping = [3];
            } else if (beatsPerMeasure === 4) {
                grouping = [4]; // Default to one group of 4 for 4/4
            }
            // 2. Handle all other simple meters (irregular or larger regular)
            else {
                // Aim for a combination of 3s and 2s.
                // This algorithm will build the grouping from right to left (end of measure first)
                // by prioritizing groups of 3s, then filling with 2s. This often results in
                // more common asymmetrical groupings (e.g., for 5: 2+3, for 7: 2+2+3).
                
                let tempRemaining = beatsPerMeasure;
                let tempGrouping = [];

                // Prioritize adding 3s until tempRemaining is 2, 1, or 0
                while (tempRemaining > 0) {
                    if (tempRemaining === 2) {
                        tempGrouping.unshift(2);
                        tempRemaining -= 2;
                    } else if (tempRemaining === 3) {
                        tempGrouping.unshift(3);
                        tempRemaining -= 3;
                    } else if (tempRemaining > 3 && tempRemaining !== 4) { // For remaining > 3, try 3s if not 4
                         tempGrouping.unshift(3);
                         tempRemaining -= 3;
                    } else if (tempRemaining === 4) { // Treat 4 as 2+2
                        tempGrouping.unshift(2, 2);
                        tempRemaining -= 4;
                    } else { // Should only be 1 if beatsPerMeasure was initially 1, but for safety
                        tempGrouping.unshift(tempRemaining);
                        tempRemaining = 0;
                    }
                }
                grouping = tempGrouping;
                // BUGFIX: Set the feeling to the root key, not the full translation key.
                feeling = 'asymmetric'; // Most common feeling for these generated irregulars
            }

            // Calculate strong beats based on the determined grouping
            const strongBeats = MetronomeEngine.calculateStrongBeatsFromGrouping(grouping);

            return { grouping, strongBeats, feeling };
        }
    }

    static generateProgram(type, options) {
        const { startBpm, targetBpm, increment = 5, totalSteps, customPoints } = options;
        let steps = [];
        switch (type) {
            case 'pyramid':
                steps = this.generatePyramidProgram(startBpm, targetBpm, totalSteps);
                break;
            case 'consolidation':
                steps = this.generateConsolidationProgram(startBpm, targetBpm, totalSteps);
                break;
            case 'endurance':
                steps = this.generateEnduranceProgram(targetBpm, totalSteps);
                break;
            case 'speed':
                steps = this.generateSpeedProgram(targetBpm, totalSteps);
                break;
            case 'agility':
                steps = this.generateAgilityProgram(startBpm, targetBpm, totalSteps);
                break;
            case 'ladder':
                steps = this.generateLadderProgram(startBpm, targetBpm, increment);
                break;
            case 'custom':
                steps = [...customPoints];
                break;
            default:
                steps = Array(totalSteps).fill(targetBpm);
        }
        return steps.map(bpm => Math.max(20, Math.min(400, bpm)));
    }

    static generatePyramidProgram(startBpm, targetBpm, totalSteps) {
        const steps = [];
        const peakBpm = Math.round(targetBpm * 1.1);
        let consolidationSteps = totalSteps > 3 ? Math.floor(totalSteps * 0.25) : 0;
        if (totalSteps > 1 && consolidationSteps === 0) consolidationSteps = 1;
        const pyramidSteps = totalSteps - consolidationSteps;
        const ascentSteps = Math.ceil(pyramidSteps / 2);
        const descentSteps = pyramidSteps - ascentSteps;
        if (ascentSteps > 0) {
            for (let i = 0; i < ascentSteps; i++) {
                const progress = ascentSteps > 1 ? i / (ascentSteps - 1) : 1;
                steps.push(Math.round(startBpm + (peakBpm - startBpm) * progress));
            }
        }
        if (descentSteps > 0) {
            for (let i = 1; i <= descentSteps; i++) {
                const progress = i / descentSteps;
                steps.push(Math.round(peakBpm - (peakBpm - targetBpm) * progress));
            }
        }
        for (let i = 0; i < consolidationSteps; i++) steps.push(targetBpm);
        if (steps.length === 0) steps.push(targetBpm);
        return steps;
    }

    static generateConsolidationProgram(startBpm, targetBpm, totalSteps) { const steps = []; for (let i = 0; i < totalSteps; i++) steps.push(i % 4 === 0 ? targetBpm : startBpm); return steps; }
    static generateEnduranceProgram(targetBpm, totalSteps) { const steps = []; const rampUp = Math.min(3, Math.floor(totalSteps / 4)); for (let i = 0; i < rampUp; i++) { steps.push(Math.round(targetBpm * 0.7 + targetBpm * 0.3 * (i / (rampUp - 1 || 1)))); } while (steps.length < totalSteps) steps.push(targetBpm); return steps; }
    static generateSpeedProgram(targetBpm, totalSteps) { const steps = []; const overspeedSteps = totalSteps > 4 ? Math.floor(totalSteps * 0.25) : 0; const rampSteps = totalSteps - overspeedSteps; const startSpeed = Math.round(targetBpm * 0.7); for (let i = 0; i < rampSteps; i++) { const progress = rampSteps > 1 ? i / (rampSteps - 1) : 1; steps.push(Math.round(startSpeed + (targetBpm - startSpeed) * progress)); } for (let i = 0; i < overspeedSteps; i++) { const overspeedIncrement = (i + 1) * 2; steps.push(targetBpm + overspeedIncrement); } if (steps.length === 0) steps.push(targetBpm); return steps; }
    static generateAgilityProgram(startBpm, targetBpm, totalSteps) { const steps = []; const cruiseSpeed = Math.round((startBpm + targetBpm) / 2); for (let i = 0; i < totalSteps; i++) steps.push(i % 2 === 0 ? cruiseSpeed : targetBpm); return steps; }
    static generateLadderProgram(startBpm, targetBpm, increment) { if (!increment || increment <= 0) return [startBpm, targetBpm]; const steps = []; if (startBpm <= targetBpm) { for (let bpm = startBpm; bpm < targetBpm; bpm += increment) steps.push(bpm); } else { for (let bpm = startBpm; bpm > targetBpm; bpm -= increment) steps.push(bpm); } steps.push(targetBpm); return [...new Set(steps)]; }
}